import {
  Box,
  Button,
  Input,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
} from "@chakra-ui/react";
import { IconType, MIN_NAME_LENGTH, MAX_NAME_LENGTH, Team } from "common";
import { useState, useRef, useCallback, useMemo, useEffect } from "react";
import { HexColorPicker } from "react-colorful";
import { BehaviorSubject, throttleTime } from "rxjs";
import { AppIconList } from "../utils/AppIconList";
import { TeamBanner } from "../utils/TeamBanner";
import { THEME_COLOR_SCHEME } from "../utils/theme";
import { useSubscribeObservable } from "../utils/useSubscribeObservable";

interface TeamManageProps {
  defaultTeam?: Team;
  isLoading: boolean;
  submitButtonText: string;
  onSubmit: (params: {
    teamName: string;
    teamColor: string;
    iconColor: string;
    teamIcon: IconType;
  }) => void;
}

export const TeamManage = ({
  defaultTeam,
  isLoading,
  submitButtonText,
  onSubmit,
}: TeamManageProps) => {
  const [teamName, setTeamName] = useState("");

  const teamColorBehaviorSubjectRef = useRef(new BehaviorSubject("#aabbcc"));
  const teamColor = useSubscribeObservable(
    teamColorBehaviorSubjectRef.current.pipe(throttleTime(100))
  );
  const setTeamColor = useCallback(
    (color: string) => teamColorBehaviorSubjectRef.current.next(color),
    []
  );

  const iconColorBehaviorSubjectRef = useRef(new BehaviorSubject("#000000"));
  const iconColor = useSubscribeObservable(
    iconColorBehaviorSubjectRef.current.pipe(throttleTime(100))
  );
  const setIconColor = useCallback(
    (color: string) => iconColorBehaviorSubjectRef.current.next(color),
    []
  );

  const [teamIcon, setTeamIcon] = useState(IconType.Dragon);

  const isValid = useMemo(
    () =>
      teamName.length >= MIN_NAME_LENGTH && teamName.length <= MAX_NAME_LENGTH,
    [teamName.length]
  );

  useEffect(() => {
    if (defaultTeam) {
      setTeamName(defaultTeam.name);
      setTeamColor(defaultTeam.color);
      setIconColor(defaultTeam.iconColor);
      setTeamIcon(defaultTeam.iconType);
    }
  }, [defaultTeam, setIconColor, setTeamColor]);

  return (
    <Box display={"flex"} flexDir={"column"} alignItems={"center"}>
      {teamColor && iconColor ? (
        <TeamBanner
          width={100}
          padding={2}
          iconType={teamIcon}
          iconColor={iconColor}
          color={teamColor.replace("#", "")}
        />
      ) : null}
      <Input
        placeholder="Team Name"
        marginBottom={4}
        marginTop={4}
        value={teamName}
        onChange={(e) => setTeamName(e.target.value)}
        textAlign={"center"}
        size={"lg"}
      />
      <Tabs width={"100%"} variant={"enclosed-colored"}>
        <TabList>
          <Tab>Icon</Tab>
          <Tab>Icon Color</Tab>
          <Tab>Banner Color</Tab>
        </TabList>
        <TabPanels>
          <TabPanel>
            <AppIconList
              selectedIconType={teamIcon}
              setSelectedIconType={setTeamIcon}
            />
          </TabPanel>
          <TabPanel
            display={"flex"}
            flexDir={"column"}
            alignItems={"center"}
            paddingTop={16}
          >
            <HexColorPicker color={iconColor} onChange={setIconColor} />
          </TabPanel>
          <TabPanel
            display={"flex"}
            flexDir={"column"}
            alignItems={"center"}
            paddingTop={16}
          >
            <HexColorPicker color={teamColor} onChange={setTeamColor} />
          </TabPanel>
        </TabPanels>
      </Tabs>
      <Button
        width={"100%"}
        marginTop={4}
        colorScheme={THEME_COLOR_SCHEME}
        isDisabled={!isValid}
        isLoading={isLoading}
        onClick={() => {
          if (!teamColor || !iconColor) {
            return;
          }
          onSubmit({ teamName, teamColor, iconColor, teamIcon });
        }}
      >
        {submitButtonText}
      </Button>
    </Box>
  );
};
